import React, { useState, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Sparkles, CheckCircle2, Clock, IndianRupee, Home, Layers } from "lucide-react";

const ROOM_TYPES = [
  "Living Room",
  "Dedicated Home Theatre",
  "Media Room",
  "Bedroom",
  "Outdoor",
  "Commercial / Auditorium",
  "Other",
];

const BUDGETS = [
  "Under ₹1 Lakh",
  "₹1L – ₹3L",
  "₹3L – ₹7L",
  "₹7L – ₹15L",
  "₹15L – ₹30L",
  "Above ₹30L",
  "Not sure yet",
];

const TIMELINES = [
  "Ready to buy now",
  "Within 1 month",
  "1 – 3 months",
  "3 – 6 months",
  "Just exploring",
];

const INTERESTS = [
  "Full Home Theatre Package",
  "Speakers / Tower / Bookshelf",
  "AV Receiver / Amplifier",
  "Subwoofer",
  "Projector / Screen",
  "Soundbar",
  "Multi-room / Custom Install",
  "Other",
];

export default function Quote() {
  const [params] = useSearchParams();
  const prefillInterest = params.get("interest") || "";
  const prefillSubject = params.get("subject") || "";

  const [f, setF] = useState({
    name: "",
    email: "",
    phone: "",
    interest: prefillInterest,
    room: "",
    budget: "",
    timeline: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (prefillInterest) setF((p) => ({ ...p, interest: prefillInterest }));
  }, [prefillInterest]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name || !f.email || !f.phone) {
      toast.error("Please share your name, email and phone so we can respond.");
      return;
    }
    setLoading(true);
    try {
      const details = {
        interest: f.interest,
        room: f.room,
        budget: f.budget,
        timeline: f.timeline,
      };
      const summary = [
        f.interest && `Interest: ${f.interest}`,
        f.room && `Room: ${f.room}`,
        f.budget && `Budget: ${f.budget}`,
        f.timeline && `Timeline: ${f.timeline}`,
      ]
        .filter(Boolean)
        .join(" · ");
      await api.post("/inquiries", {
        name: f.name,
        email: f.email,
        phone: f.phone,
        subject: prefillSubject || (f.interest ? `Quote request — ${f.interest}` : "Quote request"),
        message: [summary, f.message].filter(Boolean).join("\n\n"),
        kind: "quote",
        details,
      });
      setDone(true);
      toast.success("Quote request received — we'll be in touch within 24 hours.");
    } catch {
      toast.error("Something went wrong. Please call us at +91 80189 11999.");
    } finally {
      setLoading(false);
    }
  };

  const Chip = ({ active, children, ...rest }) => (
    <button
      type="button"
      className={`px-4 py-2 rounded-full text-sm border transition ${
        active ? "bg-gold text-black border-gold" : "border-white/15 text-white/70 hover:border-gold/50 hover:text-white"
      }`}
      {...rest}
    >
      {children}
    </button>
  );

  if (done) {
    return (
      <div className="pt-32 pb-32 container-x max-w-2xl mx-auto text-center" data-testid="quote-success">
        <div className="w-16 h-16 rounded-full bg-gold/15 flex items-center justify-center mx-auto mb-6 text-gold">
          <CheckCircle2 className="w-8 h-8" />
        </div>
        <div className="kicker mb-3">Request Received</div>
        <h1 className="font-display text-4xl md:text-5xl">Thank you, <em className="not-italic gold-text">{f.name.split(" ")[0] || "friend"}.</em></h1>
        <p className="mt-6 text-white/70 leading-relaxed">
          Our atelier has received your quote request. A senior specialist will reach out on <span className="text-white">{f.phone}</span> or <span className="text-white">{f.email}</span> within one business day with a tailored recommendation.
        </p>
        <div className="mt-10 flex gap-3 justify-center flex-wrap">
          <Link to="/packages" className="btn-gold" data-testid="quote-explore-packages">Explore Packages</Link>
          <Link to="/shop" className="btn-ghost-outline" data-testid="quote-continue-shopping">Continue Browsing</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-32 pb-24 container-x" data-testid="quote-page">
      <div className="max-w-3xl mb-14">
        <div className="kicker mb-3 flex items-center gap-2">
          <Sparkles className="w-3 h-3 text-gold" /> Bespoke Quote
        </div>
        <h1 className="font-display text-5xl md:text-7xl text-white">
          Design your <em className="not-italic gold-text">cinema.</em>
        </h1>
        <p className="mt-6 text-white/70 max-w-xl leading-relaxed">
          Tell us about your space and taste. Our team curates a personalised system with transparent Indian pricing, no obligation. Response in 24 hours.
        </p>
      </div>

      <div className="grid lg:grid-cols-3 gap-8">
        <form
          onSubmit={submit}
          className="lg:col-span-2 glass rounded-3xl p-8 md:p-10 space-y-8 bg-charcoal-900/60 border border-white/10"
          data-testid="quote-form"
        >
          {/* Interest */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Layers className="w-4 h-4 text-gold" />
              <label className="text-xs uppercase tracking-[0.18em] text-white/60">What are you looking for?</label>
            </div>
            <div className="flex flex-wrap gap-2">
              {INTERESTS.map((v) => (
                <Chip key={v} active={f.interest === v} onClick={() => setF({ ...f, interest: v })} data-testid={`quote-interest-${v}`}>
                  {v}
                </Chip>
              ))}
            </div>
          </div>

          {/* Room */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Home className="w-4 h-4 text-gold" />
              <label className="text-xs uppercase tracking-[0.18em] text-white/60">Room type</label>
            </div>
            <div className="flex flex-wrap gap-2">
              {ROOM_TYPES.map((v) => (
                <Chip key={v} active={f.room === v} onClick={() => setF({ ...f, room: v })} data-testid={`quote-room-${v}`}>
                  {v}
                </Chip>
              ))}
            </div>
          </div>

          {/* Budget */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <IndianRupee className="w-4 h-4 text-gold" />
              <label className="text-xs uppercase tracking-[0.18em] text-white/60">Budget</label>
            </div>
            <div className="flex flex-wrap gap-2">
              {BUDGETS.map((v) => (
                <Chip key={v} active={f.budget === v} onClick={() => setF({ ...f, budget: v })} data-testid={`quote-budget-${v}`}>
                  {v}
                </Chip>
              ))}
            </div>
          </div>

          {/* Timeline */}
          <div>
            <div className="flex items-center gap-2 mb-3">
              <Clock className="w-4 h-4 text-gold" />
              <label className="text-xs uppercase tracking-[0.18em] text-white/60">Timeline</label>
            </div>
            <div className="flex flex-wrap gap-2">
              {TIMELINES.map((v) => (
                <Chip key={v} active={f.timeline === v} onClick={() => setF({ ...f, timeline: v })} data-testid={`quote-timeline-${v}`}>
                  {v}
                </Chip>
              ))}
            </div>
          </div>

          <div className="hairline"></div>

          {/* Contact fields */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label htmlFor="q-name" className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Full name *</label>
              <input id="q-name" type="text" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Your name" className="w-full" data-testid="quote-name" />
            </div>
            <div>
              <label htmlFor="q-email" className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Email *</label>
              <input id="q-email" type="email" required value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="you@example.com" className="w-full" data-testid="quote-email" />
            </div>
          </div>
          <div>
            <label htmlFor="q-phone" className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Phone *</label>
            <input id="q-phone" type="tel" required value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="+91 —" className="w-full" data-testid="quote-phone" />
          </div>
          <div>
            <label htmlFor="q-message" className="block text-xs uppercase tracking-[0.18em] text-white/50 mb-2">Anything else? (Room size, brand preferences, existing gear…)</label>
            <textarea id="q-message" rows="5" value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Optional — the more we know, the better we curate." className="w-full" data-testid="quote-message"></textarea>
          </div>

          <button type="submit" disabled={loading} className="btn-gold w-full justify-center disabled:opacity-60" data-testid="quote-submit">
            {loading ? "Sending…" : "Request My Quote"}
          </button>
          <p className="text-xs text-white/40 text-center">We reply within 24 hours. No spam, no commitment.</p>
        </form>

        {/* Side rail */}
        <aside className="space-y-5" data-testid="quote-side">
          {[
            { t: "Curated for you", d: "Every quote is hand-picked by a specialist — no generic bundles." },
            { t: "Transparent pricing", d: "Prices in INR, all-inclusive. Installation and calibration quoted separately." },
            { t: "Zero-obligation", d: "Ask questions, compare brands, take your time. We're patient." },
          ].map((x) => (
            <div key={x.t} className="glass rounded-2xl p-5 bg-charcoal-900/60 border border-white/10">
              <div className="kicker mb-2">{x.t}</div>
              <p className="text-sm text-white/70 leading-relaxed">{x.d}</p>
            </div>
          ))}
          <div className="rounded-2xl p-5 border border-gold/25 bg-gold/5">
            <div className="kicker mb-2 text-gold">Prefer to talk?</div>
            <a href="tel:+918018911999" className="text-white text-lg font-display block">+91 80189 11999</a>
            <a href="mailto:batraelectronics55@gmail.com" className="text-xs text-white/60 break-all hover:text-gold">batraelectronics55@gmail.com</a>
          </div>
        </aside>
      </div>
    </div>
  );
}
